import React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { RnDot } from "@remi/icons/next";

import { useEffect } from "../hooks/useEffect";
import { cn } from "../lib/utils";

const InputOTP = ({
  ref,
  className,
  containerClassName,
  ...props
}: React.ComponentPropsWithoutRef<typeof OTPInput> & { ref?: React.Ref<React.ComponentRef<typeof OTPInput>> }) => (
  <OTPInput
    ref={ref}
    containerClassName={cn("flex items-center gap-2 has-[:disabled]:opacity-50", containerClassName)}
    className={cn("disabled:cursor-not-allowed", className)}
    {...props}
  />
);
InputOTP.displayName = "InputOTP";

const InputOTPGroup = ({ className, ...props }: React.ComponentPropsWithoutRef<"div">) => (
  <div className={cn("flex items-center", className)} {...props} />
);
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = ({
  index,
  className,
  error,
  resetValue,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & {
  index: number;
  error?: boolean;
  resetValue?: () => void;
  ref?: React.Ref<React.ComponentRef<"div">>;
}) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const data = inputOTPContext.slots[index];

  useEffect(() => {
    if (error === true && data?.isActive && resetValue) {
      resetValue();
    }
  }, [data?.isActive, error, resetValue]);

  if (!data) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex h-12 w-12 items-center justify-center border-y border-r text-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md",
        data.isActive && "z-10 ring-2 ring-border-accent ring-offset-background",
        error === true
          ? "border-y-border-destructive border-r-input first:border-l-border-destructive last:border-r-border-destructive"
          : "border-input",
        className,
      )}
      {...props}
    >
      {data.char}
      {data.hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
};
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = ({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & { ref?: React.Ref<React.ComponentRef<"div">> }) => (
  <div ref={ref} role="separator" {...props} className="flex w-full justify-center text-gray-400">
    <RnDot />
  </div>
);
InputOTPSeparator.displayName = "InputOTPSeparator";

interface RemiOTPProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  className?: string;
  containerClassName?: string;
  groupClassName?: string;
  slotClassName?: string;
  error?: boolean;
  hideSeparator?: boolean;
  resetValue?: () => void;
  value: string;
  onChange: (value: string) => void;
}
/**
 * @param resetValue - Function to reset the value of the OTP input when there is an error and a slot becomes active
 */
const RemiOTP = ({
  ref,
  className,
  containerClassName,
  groupClassName,
  slotClassName,
  error,
  hideSeparator,
  resetValue,
  ...props
}: RemiOTPProps & { ref?: React.Ref<React.ComponentRef<typeof InputOTP>> }) =>
  hideSeparator ? (
    <InputOTP maxLength={6} className={className} containerClassName={containerClassName} ref={ref} {...props}>
      <InputOTPGroup className={groupClassName}>
        <InputOTPSlot index={0} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={1} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={2} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={3} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={4} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={5} error={error} resetValue={resetValue} className={slotClassName} />
      </InputOTPGroup>
    </InputOTP>
  ) : (
    <InputOTP maxLength={6} className={className} containerClassName={containerClassName} ref={ref} {...props}>
      <InputOTPGroup className={groupClassName}>
        <InputOTPSlot index={0} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={1} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={2} error={error} resetValue={resetValue} className={slotClassName} />
      </InputOTPGroup>
      <InputOTPSeparator />
      <InputOTPGroup className={groupClassName}>
        <InputOTPSlot index={3} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={4} error={error} resetValue={resetValue} className={slotClassName} />
        <InputOTPSlot index={5} error={error} resetValue={resetValue} className={slotClassName} />
      </InputOTPGroup>
    </InputOTP>
  );

RemiOTP.displayName = "RemiOTP";

export { RemiOTP };
